/**
 * Batch parser for `EventComposer` Batch mode. Mirrors the DEMO `parseLine`
 * but does substantially more — users can paste mixed-language plans and
 * still get structured rows.
 *
 * Recognised shapes (all flexible — separators are optional, fields can
 * appear in any order):
 *
 *   2026-05-12 09:00 · publish · Issue #71 — On Quiet Interfaces
 *   2026-05-13 14:00-15:30 workshop Quiet Interfaces · session 4
 *   2026-05-14, 11:00 — survey: Reader letters, May
 *   2026-05-15 18:00 thread Friday crit (every Friday)
 *   May 18 09:00 newsletter — Weekly note
 *   2026-05-20 16:30 90m booking · Office hours · Mira
 *   2026-05-21 all-day deadline · Submit Issue #72
 *   2026-05-22 thread Sprint retro       (no time → all-day)
 *   7.01：Summer class kickoff           (short month.day → default year)
 *
 *   Chinese:
 *   2026年5月12日 上午9点 publish 第 71 期 · 论安静的界面
 *   5月13日 下午2点 workshop 工作坊第 4 节
 *   5月15日 晚上7点 thread 周五合议 (每周)
 *   5月20日 全天 deadline 提交第 72 期
 *
 * Output additions vs. the older shape:
 *   - `endTime` / `durationMinutes` for explicit ranges
 *   - `allDay` flag (no time, 'all-day', '全天')
 *   - `recurrence` RRULE-ish string for 'every {day,week,month}', '每周', …
 */

import {
  DEFAULT_KIND_BEHAVIOR,
  findKindBehaviorMatch,
  type CalendarKindBehaviorMap,
} from './kindBehavior.js';

export interface BatchParsedEvent {
  readonly source: string;
  readonly error: false;
  readonly date: string; // YYYY-MM-DD
  readonly time: string; // HH:MM (`'00:00'` when allDay)
  /** Explicit end time when the user wrote a range (`09:00-10:30`). */
  readonly endTime: string | null;
  /** Explicit duration when the user wrote `90m` / `1h30m` / `2h` /
   *  `2 hours`. Mutually exclusive with endTime in the input — the
   *  parser keeps whichever the user wrote. */
  readonly durationMinutes: number | null;
  /** True when no time was given OR the line carried 'all-day' /
   *  '全天' / similar. */
  readonly allDay: boolean;
  readonly kind: string;
  readonly title: string;
  readonly groupId: string | null;
  /** RFC-5545-shaped recurrence string when the line carried a
   *  recurrence keyword. The composer's recurrence picker uses the
   *  same wire identifiers. */
  readonly recurrence: string | null;
}

export interface BatchParseError {
  readonly source: string;
  readonly error: true;
  readonly message: string;
}

export type BatchParseResult = BatchParsedEvent | BatchParseError;

const MONTHS_EN = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
];

export interface BatchParseOptions {
  readonly defaultGroupId?: string | null;
  readonly kindKeys: readonly string[];
  readonly kindBehavior?: CalendarKindBehaviorMap;
  readonly fallbackKind?: string;
  readonly defaultYear?: number;
}

export function parseBatch(
  text: string,
  options: BatchParseOptions,
): readonly BatchParseResult[] {
  const lines = text
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0 && !l.startsWith('#'));
  return lines.map((line) => parseLine(line, options));
}

export function parseLine(
  raw: string,
  options: BatchParseOptions,
): BatchParseResult {
  const {
    defaultGroupId = null,
    kindKeys,
    kindBehavior = DEFAULT_KIND_BEHAVIOR,
    fallbackKind = 'publish',
    defaultYear = new Date().getFullYear(),
  } = options;
  const source = raw;
  let line = raw.replace(/^[-•*]\s*/, '');

  // ---- Date ----
  let date: string | null = null;
  const isoM = /(\d{4}-\d{2}-\d{2})/.exec(line);
  if (isoM !== null) {
    date = isoM[1] ?? null;
    line = line.replace(isoM[0], '').trim();
  }

  if (date === null) {
    // Chinese: `2026年5月12日` or `5月12日`
    const cnYM =
      /(?:(\d{4})\s*年\s*)?(\d{1,2})\s*月\s*(\d{1,2})\s*[日号]?/.exec(line);
    if (cnYM !== null) {
      const month = cnYM[2];
      const day = cnYM[3];
      if (month !== undefined && day !== undefined) {
        const y = cnYM[1] !== undefined ? Number(cnYM[1]) : defaultYear;
        const m = Number(month);
        const d = Number(day);
        if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
          date = `${String(y)}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
          line = line.replace(cnYM[0], '').trim();
        }
      }
    }
  }

  if (date === null) {
    // English month-day, e.g. 'May 18' / 'Jul 4, 2027'.
    const monM =
      /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s+(\d{1,2})(?:[,\s]+(\d{4}))?\b/i.exec(
        line,
      );
    if (monM !== null) {
      const month = monM[1];
      const dayRaw = monM[2];
      if (month !== undefined && dayRaw !== undefined) {
        const mIdx = MONTHS_EN.indexOf(month.toLowerCase());
        const day = parseInt(dayRaw, 10);
        const year =
          monM[3] !== undefined ? parseInt(monM[3], 10) : defaultYear;
        date = `${String(year)}-${String(mIdx + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        line = line.replace(monM[0], '').trim();
      }
    }
  }

  if (date === null) {
    // Short numeric month.day, common in copied Chinese class schedules:
    // `7.01：直线方程` / `8/2 workshop`. Anchor at line-start so title
    // fragments like `2017-2018春考` cannot become accidental dates.
    const compactMd =
      /^\s*(?:(\d{4})[-./])?(\d{1,2})[-./](\d{1,2})(?:\s*[日号])?(?:\s*[（(]?(?:周|星期|礼拜)?[一二三四五六日天][)）]?)?(?=\s|[：:、，,.\-—–]|[^\d\s]|$)/.exec(
        line,
      );
    if (compactMd !== null) {
      const month = compactMd[2];
      const day = compactMd[3];
      if (month !== undefined && day !== undefined) {
        const y =
          compactMd[1] !== undefined ? Number(compactMd[1]) : defaultYear;
        const m = Number(month);
        const d = Number(day);
        if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
          date = `${String(y)}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
          line = line.replace(compactMd[0], '').trim();
        }
      }
    }
  }

  // ---- All-day signal ----
  let allDayHint = false;
  // `\b` doesn't work for CJK; match plainly. Removing the keyword from
  // the line means it can't accidentally land in the title.
  const allDayMatch = /\b(all[-\s]?day)\b/i.exec(line) ?? /全天/.exec(line);
  if (allDayMatch !== null) {
    allDayHint = true;
    line = line.replace(allDayMatch[0], '').trim();
  }
  if (
    /(?:上午|早上|凌晨|下午|晚上|傍晚|\d{1,2}\s*(?:[:：]\d{2}|点|时).*(?:[-–—~至到]))/.test(
      line,
    )
  ) {
    line = normalizeChineseClockText(line);
  }

  // ---- Time range or single time ----
  // 24h / 12h time, optionally followed by a range `-` HH:MM[am|pm].
  // We try ranges first because a successful range match is strictly
  // more informative than a single match.
  let time: string | null = null;
  let endTime: string | null = null;

  const range =
    /(?<!\d)(?:(上午|早上|凌晨|下午|晚上|傍晚)\s*)?(\d{1,2})(?:[:：](\d{2}))?\s*(am|pm)?\s*[-–—~至到]\s*(?:(上午|早上|凌晨|下午|晚上|傍晚)\s*)?(\d{1,2})(?:[:：](\d{2}))?\s*(am|pm)?(?!\d)/i.exec(
      line,
    );
  if (range !== null) {
    const startHour = range[2];
    const endHour = range[6];
    if (startHour !== undefined && endHour !== undefined) {
      const sH = parseInt(startHour, 10);
      const sM = range[3] !== undefined ? parseInt(range[3], 10) : 0;
      const sAmPm = cnAmPmToAscii(range[1]) ?? (range[4] ?? '').toLowerCase();
      const eH = parseInt(endHour, 10);
      const eM = range[7] !== undefined ? parseInt(range[7], 10) : 0;
      const eAmPm = cnAmPmToAscii(range[5]) ?? (range[8] ?? '').toLowerCase();
      const start = normalise24(sH, sM, sAmPm, null);
      const end = normaliseRangeEnd(eH, eM, eAmPm, sAmPm);
      if (start !== null && end !== null) {
        time = start;
        endTime = end;
        line = line.replace(range[0], '').trim();
      }
    }
  }

  if (time === null) {
    // Chinese AM/PM keywords. We pre-strip them so the bare HH[:MM]
    // matcher below can do its job. `上午`/`早上`/`凌晨` → AM,
    // `下午`/`晚上` → PM.
    let cnAmPm: 'am' | 'pm' | null = null;
    const cnAm = /(?:上午|早上|凌晨)/.exec(line);
    const cnPm = /(?:下午|晚上|傍晚)/.exec(line);
    if (cnPm !== null) {
      cnAmPm = 'pm';
      line = line.replace(cnPm[0], '').trim();
    } else if (cnAm !== null) {
      cnAmPm = 'am';
      line = line.replace(cnAm[0], '').trim();
    }

    const t1 = /(\d{1,2})(?:[:：]|点|时)(\d{1,2})\s*(am|pm)?/i.exec(line);
    if (t1 !== null) {
      const hour = t1[1];
      const minute = t1[2];
      if (hour !== undefined && minute !== undefined) {
        const hh = parseInt(hour, 10);
        const mm = parseInt(minute, 10);
        const norm = normalise24(hh, mm, (t1[3] ?? '').toLowerCase(), cnAmPm);
        if (norm !== null) {
          time = norm;
          line = line.replace(t1[0], '').trim();
        }
      }
    } else {
      const t2 = /(\d{1,2})\s*(?:am|pm|点|时)/i.exec(line);
      if (t2 !== null) {
        const hour = t2[1];
        if (hour !== undefined) {
          const hh = parseInt(hour, 10);
          const tail = t2[0].slice(String(hh).length).trim().toLowerCase();
          const ampm = tail === 'am' || tail === 'pm' ? tail : '';
          const norm = normalise24(hh, 0, ampm, cnAmPm);
          if (norm !== null) {
            time = norm;
            line = line.replace(t2[0], '').trim();
          }
        }
      }
    }
  }

  // ---- Duration ----
  let durationMinutes: number | null = null;
  if (endTime === null) {
    const dur = /(?:^|\s)(\d{1,3})\s*(?:m|min|mins|分钟|分)\b/i.exec(line);
    const durHr =
      /(?:^|\s)(\d{1,2})\s*(?:h|hr|hrs|hour|hours|小时)\s*(?:(\d{1,2})\s*(?:m|min|mins|分钟|分)?)?/i.exec(
        line,
      );
    if (durHr !== null) {
      const hour = durHr[1];
      if (hour === undefined)
        return { source, error: true, message: 'Invalid duration' };
      const h = parseInt(hour, 10);
      const m = durHr[2] !== undefined ? parseInt(durHr[2], 10) : 0;
      durationMinutes = h * 60 + m;
      line = line.replace(durHr[0], ' ').trim();
    } else if (dur !== null) {
      const minutes = dur[1];
      if (minutes !== undefined) {
        durationMinutes = parseInt(minutes, 10);
        line = line.replace(dur[0], ' ').trim();
      }
    }
  }

  // ---- Recurrence ----
  let recurrence: string | null = null;
  // English: 'every Monday' / 'every week' / 'weekly' / 'biweekly' / 'daily' / 'monthly'
  const enEvery =
    /\b(every\s+(?:day|week|month|sun|mon|tue|wed|thu|fri|sat|monday|tuesday|wednesday|thursday|friday|saturday|sunday)|daily|weekly|biweekly|fortnightly|monthly|repeats|repeat\s+weekly|repeat\s+daily)\b/i.exec(
      line,
    );
  if (enEvery !== null) {
    const m = enEvery[0].toLowerCase();
    if (m.includes('biweekly') || m.includes('fortnightly')) {
      recurrence = 'FREQ=WEEKLY;INTERVAL=2';
    } else if (m.includes('month')) {
      recurrence = 'FREQ=MONTHLY';
    } else if (
      m.includes('day') ||
      m === 'daily' ||
      m.includes('repeat daily')
    ) {
      recurrence = 'FREQ=DAILY';
    } else {
      recurrence = 'FREQ=WEEKLY';
    }
    line = line.replace(enEvery[0], ' ').trim();
  } else {
    // Chinese
    const cnEvery = /每\s*(天|周|周[一二三四五六日天]|月|两周|双周)/.exec(line);
    if (cnEvery !== null) {
      const tail = cnEvery[1] ?? '';
      if (tail === '天') recurrence = 'FREQ=DAILY';
      else if (tail === '月') recurrence = 'FREQ=MONTHLY';
      else if (tail === '两周' || tail === '双周')
        recurrence = 'FREQ=WEEKLY;INTERVAL=2';
      else recurrence = 'FREQ=WEEKLY';
      line = line.replace(cnEvery[0], ' ').trim();
    }
  }

  // ---- Kind ----
  let kind: string | null = null;
  const kindMatch = findKindBehaviorMatch(line, kindKeys, kindBehavior);
  if (kindMatch !== null) {
    kind = kindMatch.kind;
    line = removeMatch(line, kindMatch.index, kindMatch.length);
  }

  // Strip leftover separators / punctuation (incl. CJK separators ·、，).
  line = line
    .replace(
      /[（(]\s*(?:每周重复|重复|每周|每日|每月|weekly|daily|monthly|biweekly|fortnightly|repeats?|repeat\s+(?:daily|weekly|monthly)|全天|all[-\s]?day)\s*[)）]/gi,
      '',
    )
    .replace(/[（(]\s*[)）]/g, '')
    .replace(/^[—–\-·:、，,\s]+|[—–\-·:、，,\s]+$/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  if (date === null) {
    return { source, error: true, message: 'No date detected' };
  }

  const allDay = allDayHint || time === null;
  return {
    source,
    error: false,
    date,
    time: time ?? '00:00',
    endTime,
    durationMinutes,
    allDay,
    kind: kind ?? fallbackKind,
    title: line.length > 0 ? line : 'Untitled event',
    groupId: defaultGroupId,
    recurrence,
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function removeMatch(text: string, index: number, length: number): string {
  return `${text.slice(0, index)} ${text.slice(index + length)}`.trim();
}

function cnAmPmToAscii(raw: string | undefined): 'am' | 'pm' | null {
  if (raw === undefined) return null;
  return /下午|晚上|傍晚/.test(raw) ? 'pm' : 'am';
}

function normaliseRangeEnd(
  hour: number,
  minute: number,
  explicitAmPm: string,
  inheritedAmPm: string,
): string | null {
  // '上午10:10-12:10' means 12:10 noon, not 00:10. Keep explicit '12am'
  // semantics, but do not turn an unmarked inherited-AM range end into
  // midnight.
  if (explicitAmPm.length === 0 && inheritedAmPm === 'am' && hour === 12) {
    return normalise24(hour, minute, '', null);
  }
  return normalise24(hour, minute, explicitAmPm, inheritedAmPm);
}

function normalizeChineseClockText(source: string): string {
  return source
    .replace(/(\d{1,2})：(\d{2})/g, '$1:$2')
    .replace(
      /(\d{1,2})\s*(?:点|时)\s*(?:(\d{1,2})\s*分?)?/g,
      (_match: string, hour: string, minute: string | undefined): string =>
        `${hour}:${String(minute === undefined ? 0 : Number(minute)).padStart(2, '0')}`,
    );
}

/** Convert a 12h or 24h hour into a canonical 24h `HH:MM`. Returns null
 *  for nonsensical inputs (hour > 23 with no AM/PM disambiguation,
 *  minute > 59). The `inheritedAmPm` lets the END of a range borrow the
 *  AM/PM marker from the START, which matches how people actually
 *  write ranges ('9-10:30am' means both AM). */
function normalise24(
  hour: number,
  minute: number,
  ampm: string,
  inheritedAmPm: string | null,
): string | null {
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  if (minute < 0 || minute > 59) return null;
  let h = hour;
  const tag = ampm.length > 0 ? ampm : (inheritedAmPm ?? '');
  if (tag === 'pm' && h < 12) h += 12;
  if (tag === 'am' && h === 12) h = 0;
  if (h < 0 || h > 23) return null;
  return `${String(h).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}
